import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetRecordTypeQuery, useListRecordsQuery, useMeQuery } from '@platform/state/api';
import type { IFieldDefinition } from '@platform/core/types';
import { todayIso } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { isArticleType } from '../../tabs/article/articleToc';
import { useValueText } from '../lookup/useValueText';
import { valueText } from '../options/valueText';
import type { IMdQueryProps } from './IMdQueryProps';
import { mdQueryParams, mdQuerySpec } from './mdQuerySpec';

/**
 * Живий запит у статті (UIUX/field-types/rich-text; прототип kbQuery): рамка з шапкою «⟳ Назва · живе подання» і лічильником,
 * таблиця ID (посилання) + колонки специфікації; дані — з API списку з фільтрами запиту. Невідомий тип — повідомлення червоним.
 */
export function MdQuery({ lines, ws }: IMdQueryProps) {
  const { t } = useTranslation();
  const spec = useMemo(() => mdQuerySpec(lines), [lines]);
  const { data: meta, isError } = useGetRecordTypeQuery(spec.module);
  const { data: me } = useMeQuery();
  const rt = meta?.recordType;
  const params = rt ? mdQueryParams(spec, rt, me?.id ?? '', todayIso()) : null;
  const { data } = useListRecordsQuery({ ws, rt: spec.module, params: params ?? {} }, { skip: !params });
  const moduleKey = meta?.module.module.key ?? spec.module;
  const strings = useModuleStrings(meta?.module.module.key);
  const fmt = useValueText(strings, spec.module);
  if (isError) return <div className="md-query md-query--error">{t('fieldControls.mdUnknownModule', { module: spec.module })}</div>;
  if (!rt) return <div className="md-query md-query--loading">{t('fieldControls.mdLiveQuery')}</div>;
  const cols = spec.columns.map((k) => rt.fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => Boolean(f));
  const items = (data?.items ?? []).slice(0, spec.limit);
  const total = data?.total ?? 0;
  const name = strings.entity(spec.module, 'name', rt.name);
  const tab = isArticleType(rt) ? 'read' : undefined;
  return (
    <div className="md-query">
      <div className="md-query__head">
        <Link to={routes.list(moduleKey, spec.module)}>⟳ {t('fieldControls.mdLiveView', { name })}</Link>
        <span className="md-query__count">{total > spec.limit ? t('fieldControls.countOf', { count: spec.limit, total }) : t('fieldControls.records', { count: total })}</span>
      </div>
      <div className="md-query__scroll">
        <table className="md-query__table">
          <thead><tr><th>ID</th>{cols.map((c) => <th key={c.key}>{strings.field(spec.module, c.key, c.label)}</th>)}</tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td><Link className="md-query__code" to={routes.record(moduleKey, spec.module, r.id, tab)}>{r.display}</Link></td>
                {cols.map((c) => (
                  <td key={c.key}>{c.key === 'title'
                    ? <Link className="md-query__title" to={routes.record(moduleKey, spec.module, r.id, tab)}>{r.title || t('fieldControls.untitled')}</Link>
                    : (c.key === 'status' ? strings.status(spec.module, r.status, rt.statuses.list.find((s) => s.value === r.status)?.label) : valueText(c, rt, r.values[c.key], fmt)) || '—'}</td>
                ))}
              </tr>
            ))}
            {data && !items.length && <tr><td colSpan={cols.length + 1} className="md-query__empty">{t('fieldControls.mdNoRecords')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
